import {Component} from '@angular/core';
import {Role} from '../../model/auth/Role';
import {MobileAwareComponent} from '../../common/mobile-aware/mobile-aware.component';
import {BreakpointObserver} from '@angular/cdk/layout';
import {Contest} from '../../model/contest/Contest';
import {NotificationHandler} from '../../helper/notification.handler';
import {ContestService} from '../../service/contest.service';
import {
  ContestDetailsData,
  ContestDetailsModalComponent
} from '../contest-details-modal/contest-details-modal.component';
import {MatDialog} from '@angular/material/dialog';
import {LocalStorageService} from 'ngx-webstorage';
import {
  DeleteConfirmationData,
  DeleteConfirmationModalComponent,
  DeleteContext
} from '../../common/modal/delete-confirmation-modal/delete-confirmation-modal.component';
import {TenantService} from '../../service/tenant.service';
import {ErrorInterceptor} from '../../helper/error.interceptor';

interface ContestMobileDetailsData {
  contest: Contest;
}

@Component({
  selector: 'app-contests-manage-list',
  templateUrl: './contests-manage-list.component.html',
  styleUrls: ['./contests-manage-list.component.scss']
})
export class ContestsManageListComponent extends MobileAwareComponent {

  readonly roles = [Role.Host];

  contests!: Contest[] | null;

  competitionId: string | undefined;

  editAllowed = true;

  constructor(private contestService: ContestService,
              private tenantService: TenantService,
              private localStorageService: LocalStorageService,
              private dialog: MatDialog,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  initContent() {
    const tenant = this.tenantService.getTenant();
    if (tenant && tenant.id) {
      this.competitionId = tenant.id;
      this.loadContests(this.competitionId);
    }
  }

  loadContests(competitionId: string) {
    this.contests = null;

    this.contestService.getContests(competitionId).subscribe(contests => {
      this.contests = contests;
    }, error => NotificationHandler.handleError($localize`:Notifications|Contests fetch has failed message@@notifications-contests-fetch-failed-error:Contests fetch has failed`, error));
  }

  createContest() {
    if (this.competitionId) {
      const creationData: ContestDetailsData = {
        competitionId: this.competitionId,
        readOnly: false,
      };
      this.dialog.open(ContestDetailsModalComponent, {
        width: '600px',
        data: creationData
      }).afterClosed().subscribe((data: ContestDetailsData) => {
        if (data && data.contest) {
          this.loadContests(data.competitionId);
        }
      });
    }
  }

  editContest(contest: Contest) {
    const data: ContestDetailsData = {
      competitionId: <string>this.competitionId,
      contest: contest,
      readOnly: false,
    };
    this.dialog.open(ContestDetailsModalComponent, {
      width: '600px',
      data
    }).afterClosed().subscribe((data: ContestDetailsData) => {
      if (data && data.competitionId) {
        this.loadContests(data.competitionId);
      }
    });
  }

  deleteContest(contest: Contest) {
    const data: DeleteConfirmationData = {
      title: $localize`:Contests Details|Contests details delete item confirmation title@@contests-details-delete-item-confirmation-title:Contest Delete`,
      itemType: $localize`:Contests Details|Contests details delete item type@@contests-details-delete-item-type:Contest`,
      name: contest.name,
      actionDesc: $localize`:Contests Details|Contests details delete item prompt@@contests-details-delete-item-prompt:will be removed from the system and all assigned users`,
      archive: false
    };
    const dialogRef = this.dialog.open(DeleteConfirmationModalComponent, {
      width: '600px',
      data: data
    });

    dialogRef.componentInstance.deleteConfirmed.subscribe((context: DeleteContext) => {
      this.contestService.delete(contest.id).subscribe(() => {
          context.modal.deleteHandled();

          this.loadContests(this.competitionId || '');
        },
        error => {
          const code = ErrorInterceptor.getStatusCode(error);

          if (code == 409) {
            NotificationHandler.handleWarning($localize`:Notifications|Contest delete was not successful due to a conflict message@@notifications-contest-delete-failed-due-to-conflict-error:The contest cannot be deleted. Most likely, it already contains participant registrations`, error, false);

            context.modal.stopProgress();
            context.modal.closeModal();
          } else {
            context.modal.deleteHandled($localize`:Notifications|Contest delete has failed message@@notifications-contest-delete-failed-error:Contest delete has failed`, error);
          }
        });
    });
  }

  rolesUpdated(roles: Role[]) {
    this.editAllowed = roles.includes(Role.Host);
  }
}
